import React, {useCallback, useEffect, useRef, useState} from "react"
import {useNavigate} from "react-router-dom"
import {Box, Typography} from "@mui/material"

import {resolveCustomerPath} from "../../utils/resolveCustomerPath"
import {CustomerResultsTable} from "../CustomerResultsTable"

import {AdvancedSearchPanel, ClientOption} from "./AdvancedSearchPanel"

import {api, AdvancedSearchParams, CustomerSearchResponse} from "@indebted/api"

type AdvancedSearchContainerProps = {
	// Called once the client list resolves, with whether any client offers custom
	// fields. The page above states the narrowing rule and has no client data of
	// its own, so it cannot otherwise know whether that rule includes custom
	// fields — and a rule stated incompletely is worse than one stated late.
	onCustomFieldsAvailable?: (available: boolean) => void
	hidden: boolean
	backSearch: string
}

type ResultsState = {
	results: CustomerSearchResponse
	loading: boolean
	error: string
	submitted: boolean
}

const initialState: ResultsState = {results: [], loading: false, error: "", submitted: false}

function AdvancedSearchContainer({hidden, backSearch, onCustomFieldsAvailable}: AdvancedSearchContainerProps) {
	const [clients, setClients] = useState<ClientOption[]>([])
	// Held in a ref so the fetch effect can depend on nothing and still call the
	// current callback. Requiring callers to memoize would put the cost of one
	// component's implementation detail onto every one of them, and nothing would
	// enforce it — an inline arrow would type-check and then refetch per render.
	const reportCustomFieldsAvailable = useRef(onCustomFieldsAvailable)
	useEffect(() => {
		reportCustomFieldsAvailable.current = onCustomFieldsAvailable
	}, [onCustomFieldsAvailable])
	const [state, setState] = useState<ResultsState>(initialState)
	const navigate = useNavigate()
	const mounted = useRef(true)

	// Track mount state so async resolutions (search / customer fetch) never call
	// navigate or setState after the page has unmounted — same intent as the `active`
	// flag in the client-list effect below, scoped to the component lifetime.
	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	// Dedicated lightweight clients endpoint — returns only the client list without
	// assembling case work-items (unlike the Cases work-queue endpoint).
	useEffect(() => {
		let active = true
		api.clients
			.list()
			.then((data) => {
				if (active) {
					setClients(
						data.map((client) => ({
							ID: client.ID,
							Name: client.Name,
							// Comes with the client, so choosing one populates the field
							// picker without a request. Absent means the client has recorded
							// no custom fields, and the panel hides the section.
							CustomFields: client.CustomFields ?? [],
						})),
					)
					reportCustomFieldsAvailable.current?.(data.some((client) => (client.CustomFields?.length ?? 0) > 0))
				}
			})
			.catch(() => {})
		return () => {
			active = false
		}
		// Deliberately empty: the client list is fetched once. The callback is read
		// through a ref rather than listed here, so a caller passing an inline
		// function cannot turn one fetch into one per render.
	}, [])

	const navigateToCustomer = useCallback(
		(id: string) => {
			const back = `/customers${encodeURIComponent(backSearch)}`
			return api.customer
				.get(id, new Date().getTimezoneOffset())
				.then((customer) => {
					if (mounted.current) navigate(`${resolveCustomerPath(customer)}?back=${back}`)
				})
				.catch(() => {
					if (mounted.current) navigate(`/customers/${id}?back=${back}`)
				})
		},
		[navigate, backSearch],
	)

	const handleSubmit = useCallback(
		(params: AdvancedSearchParams) => {
			setState({results: [], loading: true, error: "", submitted: true})
			api.customer
				.advancedSearch(params)
				.then((results) => {
					if (!mounted.current) return
					setState({results, loading: false, error: "", submitted: true})
					if (results.length === 1) {
						return navigateToCustomer(results[0].ID)
					}
				})
				.catch((error) => {
					if (!mounted.current) return
					setState({results: [], loading: false, error: error?.Message || "Search failed", submitted: true})
				})
		},
		[navigateToCustomer],
	)

	const showEmptyMessage = state.submitted && !state.loading && !state.error && state.results.length === 0

	return (
		<Box hidden={hidden} mt={2}>
			<AdvancedSearchPanel
				clients={clients}
				onSubmit={handleSubmit}
				onClear={() => setState(initialState)}
				loading={state.loading}
			/>
			{state.error && (
				<Typography color="error" sx={{mt: 2}}>
					{state.error}
				</Typography>
			)}
			{showEmptyMessage && <Typography sx={{mt: 2}}>No customers found</Typography>}
			<CustomerResultsTable
				customers={state.results}
				onRowClick={(customer) => navigateToCustomer(customer.ID)}
			/>
		</Box>
	)
}

export {AdvancedSearchContainer}
export type {AdvancedSearchContainerProps}
